import {deleteAnalysisSteps} from 'analysis-tool/AnalysisActions';
import {AnalysisGraphQL, AnalysisViewGraphQL} from 'analysis-tool/AnalysisTypes';
import {OverflowDataViewAction} from 'analysis-tool/data-view/DataViewContext';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import getDefaultMainViewItemForAnalysis from 'analysis-tool/ui/getDefaultMainViewItemForAnalysis';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import {DeletePipelineStepsBehavior} from 'pipelines/PipelineDAO';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import {withLoadingScreen} from 'util/Promises';

import {isTopLevelAnalysisView} from '../AnalysisViewUtils';

export default function getDeleteViewAction(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL,
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void,
  onChangeSelectedRightSidebarItem: (newSelectedItem: AnalysisRightSidebarItem | null) => void
): OverflowDataViewAction | null {
  if (isAnalysisReadOnly(analysis)) {
    return null;
  }

  const viewType = isTopLevelAnalysisView(view) ? 'table' : 'view';
  const firstStep = view.lineage[0].step;
  const {canEdit, message} = checkCanModifyStep(analysis, firstStep.id, viewType, true);

  return {
    icon: 'delete',
    onClick: async () => {
      const confirmed = await runConfirmDeleteModal({typeName: 'view'});
      if (!confirmed) {
        return;
      }
      return withLoadingScreen(
        onDelete(analysis.id, firstStep.id, onChangeSelectedItem, onChangeSelectedRightSidebarItem)
      );
    },
    label: `Delete ${viewType}`,
    isDisabled: !canEdit,
    helpText: !canEdit ? message : undefined,
  };
}

async function onDelete(
  analysisId: string,
  stepId: string,
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void,
  onChangeSelectedRightSidebarItem: (newSelectedItem: AnalysisRightSidebarItem | null) => void
): Promise<void> {
  // Set to empty analysis view first so we can safely delete the current view without causing any rendered
  // components that relied on the existing view to exist to error
  onChangeSelectedItem({type: AnalysisMainViewItemType.EMPTY_ANALYSIS});
  onChangeSelectedRightSidebarItem(null);
  const updatedAnalysis = await deleteAnalysisSteps(analysisId, {
    stepIds: [stepId],
    behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
  });
  onChangeSelectedItem(getDefaultMainViewItemForAnalysis(updatedAnalysis));
}
